import Link from 'next/link'
import { useRouter } from 'next/router'
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import { AnyObject, Form } from 'react-final-form'

import { faEye } from '@fortawesome/pro-light-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash'
import { SubmissionErrors } from 'final-form'
import { object } from 'yup'

import { DEVELOPER_DASHBOARD_URL } from '@infura/packages.common/constants'
import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import Notification from '@infura/packages.components/Notification'
import { Button } from '@infura/packages.ui/Button'

import { CaptchaModal } from 'components/CaptchaModal'
import FieldInput from 'components/FieldInput'
import FooterLinksForm from 'components/wallet/FooterLinksForm'

import { emailValidator, getValidationErrors, passwordValidator } from 'lib/forms/validators'
import { Infura } from 'lib/types/infuraTypes'

const validationSchema = object().shape({
  email: emailValidator,
  password: passwordValidator
})

export default function LoginWalletLinkingForm({
  onSubmit,
  setLoginStep
}: {
  onSubmit: ({ email, password, recaptcha, ...other }) => Promise<boolean | AnyObject | SubmissionErrors>
  setLoginStep: Dispatch<SetStateAction<Infura.AUTH_LOGIN_STEP>>
}) {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [captchaModalOpen, setCaptchaModalOpen] = useState(false)
  const { asPath } = useRouter()

  useEffect(() => {
    const initPageView = async () => {
      window.analytics.page(asPath, {
        page_name: 'Metamask Login - Email password linking',
        user_experience: 'B'
      })
    }
    if (window?.analytics) initPageView()
  }, [asPath])

  const validateLogin = async values => {
    return await getValidationErrors({
      validationSchema,
      values
    })
  }

  const initClickEventProps = {
    location: 'Dashboard Login Page',
    type: 'Text'
  }

  return (
    <>
      <h1 className='font-body xxl:text-4xl mb-8 text-3xl font-bold'>
        Link MetaMask wallet with your MetaMask Developer account
      </h1>

      <Form
        initialValues={{ authLoginMethod: Infura.AUTH_LOGIN_METHOD.EMAIL_PASSWORD }}
        onSubmit={onSubmit}
        render={({ handleSubmit, hasValidationErrors, submitError, submitting }) => (
          <Fragment>
            {submitError ? (
              <Notification className='mb-6' color='alert' data-testid='auth-form-error' size='sm' variant='outlined'>
                <span className='w-full text-center'>{submitError}</span>
              </Notification>
            ) : null}
            <form className='xxl:text-lg text-base' data-testid='auth-form' onSubmit={handleSubmit}>
              <div>
                <FieldInput
                  autoComplete='email'
                  className='xxl:text-lg mb-6 h-auto px-4 py-3 text-base'
                  data-testid='field_email'
                  id='email'
                  label={
                    <span className='xxl:mb-2 xxl:text-lg text-text-default font-mono text-base uppercase'>Email</span>
                  }
                  name='email'
                  required
                  type='email'
                />
                <FieldInput
                  autoComplete='current-password'
                  className='xxl:text-lg h-auto px-4 py-3 text-base'
                  data-testid='field_password'
                  icon={passwordVisible ? faEye : faEyeSlash}
                  iconPosition='right'
                  id='password'
                  label={
                    <span className='xxl:mb-2 xxl:text-lg text-text-default font-mono text-base uppercase'>
                      Password
                    </span>
                  }
                  name='password'
                  onIconClick={() => setPasswordVisible(!passwordVisible)}
                  required
                  type={passwordVisible ? 'text' : 'password'}
                />
                <div className='mb-10 mt-6'>
                  <Link
                    className='xxl:text-lg 2xxl:text-xl block text-base'
                    href='/forgotpassword'
                    onClick={() =>
                      trackClickForSegmentAnalytics({
                        ...initClickEventProps,
                        clickText: 'Forgot your password?',
                        clickUrl: `${DEVELOPER_DASHBOARD_URL}/forgotpassword`
                      })
                    }
                    passHref
                  >
                    Forgot your password?
                  </Link>
                </div>
                <p className='max-w-xl pr-8'>
                  MetaMask Developer will only collect and store your MetaMask Profile ID which is a unique, anonymized
                  identifier. No personal information or wallet addresses are accessed or stored.
                </p>
                <div className='flex space-x-3'>
                  <Button
                    className='mb-8'
                    data-testid='cancel-button'
                    disabled={submitting}
                    onClick={() => setLoginStep(Infura.AUTH_LOGIN_STEP.LOGIN)}
                    theme='secondary'
                    type='button'
                  >
                    Cancel
                  </Button>
                  <Button
                    className='mb-8'
                    data-testid='auth-button'
                    disabled={submitting}
                    loading={submitting}
                    onClick={() => {
                      if (!hasValidationErrors) setCaptchaModalOpen(true)
                    }}
                    type='button'
                  >
                    Agree and link
                  </Button>
                  <CaptchaModal open={captchaModalOpen} setIsOpen={setCaptchaModalOpen} />
                </div>
              </div>
            </form>
          </Fragment>
        )}
        validate={validateLogin}
      />

      <FooterLinksForm />
    </>
  )
}
